import { useState } from 'react';
import { formatCurrency } from '../../utils/formatters';
import { useTheme } from '../../context/ThemeContext';
import { getThemeClasses } from '../../utils/theme';
import Footer from '../common/Footer';

const ReportsDashboard = () => {
  const { isDarkMode } = useTheme();
  const theme = getThemeClasses(isDarkMode);
  const [dateRange, setDateRange] = useState('30days');

  const reportMetrics = {
    totalReviewed: 1247,
    totalApproved: 1089,
    totalDenied: 158,
    approvalRate: 87.3,
    avgReviewTime: 4.2,
    totalAmount: 2847500,
  };

  const topProviders = [
    { name: 'Veterans Evaluation Services (VES)', count: 145, amount: 425000 },
    { name: 'OptumServe Health Services (OSHS)', count: 132, amount: 398000 },
    { name: 'Loyal Source Government Services (LSGS)', count: 98, amount: 287000 },
    { name: 'Leidos QTC Health Services (QTC)', count: 87, amount: 256000 },
  ];

  const denialReasons = [
    { reason: 'Insufficient Documentation', count: 45, percentage: 28.5 },
    { reason: 'Out of Policy', count: 38, percentage: 24.1 },
    { reason: 'Duplicate Payment', count: 32, percentage: 20.3 },
    { reason: 'Incorrect Coding', count: 25, percentage: 15.8 },
    { reason: 'Other', count: 18, percentage: 11.3 },
  ];

  return (
    <div className={`min-h-screen ${theme.bgPrimary} flex flex-col`}>
      <div className="flex-1">
        {/* Page Header */}
      <div className={`${theme.bgSecondary} border-b ${theme.border} px-6 py-4`}>
        <div className="flex items-center justify-between">
          <div>
            <h2 className={`text-2xl font-bold ${theme.textPrimary}`}>Reports & Analytics</h2>
            <p className={`mt-1 text-sm ${theme.textTertiary}`}>Performance metrics and insights</p>
          </div>
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className={`px-4 py-2 text-sm ${theme.input} rounded-md focus:ring-2 focus:ring-blue-500`}
          >
            <option value="7days">Last 7 Days</option>
            <option value="30days">Last 30 Days</option>
            <option value="90days">Last 90 Days</option>
            <option value="year">This Year</option>
          </select>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className={`${theme.card} rounded-lg border shadow p-6`}>
            <h3 className={`text-sm font-medium ${theme.textTertiary}`}>
              Total Reviewed
            </h3>
            <p className={`mt-2 text-3xl font-bold ${theme.textPrimary}`}>
              {reportMetrics.totalReviewed}
            </p>
          </div>
          <div className={`${theme.card} rounded-lg border shadow p-6`}>
            <h3 className={`text-sm font-medium ${theme.textTertiary}`}>
              Approval Rate
            </h3>
            <p className="mt-2 text-3xl font-bold text-green-500">
              {reportMetrics.approvalRate}%
            </p>
          </div>
          <div className={`${theme.card} rounded-lg border shadow p-6`}>
            <h3 className={`text-sm font-medium ${theme.textTertiary}`}>
              Avg Review Time
            </h3>
            <p className="mt-2 text-3xl font-bold text-blue-500">
              {reportMetrics.avgReviewTime}m
            </p>
          </div>
        </div>

        {/* Top Providers */}
        <div className={`${theme.card} rounded-lg border shadow p-6`}>
          <h3 className={`text-lg font-semibold ${theme.textPrimary} mb-4`}>
            Top Providers by Volume
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className={theme.bgTertiary}>
                <tr>
                  <th className={`px-4 py-3 text-left text-xs font-medium ${theme.textTertiary} uppercase`}>
                    Provider
                  </th>
                  <th className={`px-4 py-3 text-left text-xs font-medium ${theme.textTertiary} uppercase`}>
                    Cases
                  </th>
                  <th className={`px-4 py-3 text-left text-xs font-medium ${theme.textTertiary} uppercase`}>
                    Total Amount
                  </th>
                </tr>
              </thead>
              <tbody className={`divide-y ${theme.borderLight}`}>
                {topProviders.map((provider, index) => (
                  <tr key={index} className={theme.cardHover}>
                    <td className={`px-4 py-3 text-sm font-medium ${theme.textPrimary}`}>
                      {provider.name}
                    </td>
                    <td className={`px-4 py-3 text-sm ${theme.textSecondary}`}>
                      {provider.count}
                    </td>
                    <td className={`px-4 py-3 text-sm font-semibold ${theme.textPrimary}`}>
                      {formatCurrency(provider.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Denial Reasons */}
        <div className={`${theme.card} rounded-lg border shadow p-6`}>
          <h3 className={`text-lg font-semibold ${theme.textPrimary} mb-4`}>
            Top Denial Reasons
          </h3>
          <div className="space-y-4">
            {denialReasons.map((item, index) => (
              <div key={index}>
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-sm font-medium ${theme.textPrimary}`}>
                    {item.reason}
                  </span>
                  <span className={`text-sm ${theme.textSecondary}`}>
                    {item.count} ({item.percentage}%)
                  </span>
                </div>
                <div className={`w-full ${theme.bgTertiary} rounded-full h-2`}>
                  <div
                    className="bg-blue-500 h-2 rounded-full"
                    style={{ width: `${item.percentage}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Payment Status Breakdown */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className={`${theme.card} rounded-lg border shadow p-6`}>
            <h3 className={`text-lg font-semibold ${theme.textPrimary} mb-4`}>
              Payment Status
            </h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className={`text-sm ${theme.textSecondary}`}>Approved</span>
                <span className="text-sm font-semibold text-green-500">
                  {reportMetrics.totalApproved}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className={`text-sm ${theme.textSecondary}`}>Denied</span>
                <span className="text-sm font-semibold text-red-500">
                  {reportMetrics.totalDenied}
                </span>
              </div>
              <div className={`flex items-center justify-between pt-3 border-t ${theme.border}`}>
                <span className={`text-sm font-medium ${theme.textPrimary}`}>
                  Total
                </span>
                <span className={`text-sm font-bold ${theme.textPrimary}`}>
                  {reportMetrics.totalReviewed}
                </span>
              </div>
            </div>
          </div>

          <div className={`${theme.card} rounded-lg border shadow p-6`}>
            <h3 className={`text-lg font-semibold ${theme.textPrimary} mb-4`}>
              Financial Summary
            </h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className={`text-sm ${theme.textSecondary}`}>
                  Total Processed
                </span>
                <span className={`text-sm font-semibold ${theme.textPrimary}`}>
                  {formatCurrency(reportMetrics.totalAmount)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className={`text-sm ${theme.textSecondary}`}>Average Payment</span>
                <span className={`text-sm font-semibold ${theme.textPrimary}`}>
                  {formatCurrency(
                    reportMetrics.totalAmount / reportMetrics.totalReviewed
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>

      <Footer />
    </div>
  );
};

export default ReportsDashboard;
